import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { wsClient } from '../../services/websocket-client.js';
import { AudioCaptureService } from '../../services/audio-capture-service.js';
import { AudioPlaybackService } from '../../services/audio-playback-service.js';
import '../voice/cp-audio-visualizer.js';
import '../voice/cp-call-controls.js';

type OverlayState = 'connecting' | 'active' | 'ended' | 'error';
type InputMode = 'ptt' | 'vad';

@customElement('cp-chat-voice-overlay')
export class CpChatVoiceOverlay extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) conversationId = 0;

  @state() private _callState: OverlayState = 'connecting';
  @state() private _sessionId = '';
  @state() private _isMuted = false;
  @state() private _inputMode: InputMode = 'vad';
  @state() private _isAgentSpeaking = false;
  @state() private _textMode = false;
  @state() private _micAvailable = true;
  @state() private _errorMessage = '';
  @state() private _callDuration = 0;

  private _audioCapture: AudioCaptureService | null = null;
  private _audioPlayback: AudioPlaybackService | null = null;
  private _durationTimer: ReturnType<typeof setInterval> | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._onWsMessage);
    this._startCall();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._onWsMessage);
    this._stopDurationTimer();
    this._stopAudio();
    if (this._sessionId && this._callState === 'active') {
      wsClient.sendVoiceCallEnd(this._sessionId);
    }
  }

  private _startCall(): void {
    this._callState = 'connecting';
    this._sessionId = '';
    if (wsClient.connectionState !== 'connected') {
      this._callState = 'error';
      this._errorMessage = 'WebSocket nicht verbunden';
      return;
    }
    wsClient.sendVoiceCallStart(undefined, this.conversationId || undefined);
  }

  private async _initAudio(): Promise<void> {
    this._audioPlayback = new AudioPlaybackService();
    this._audioPlayback.on('stateChange', (playbackState) => {
      this._isAgentSpeaking = playbackState === 'playing';
    });

    this._audioCapture = new AudioCaptureService();
    this._audioCapture.on('chunk', this._onAudioChunk);
    this._audioCapture.on('error', (err) => {
      console.error('Audio capture error:', err.message);
    });

    if (this._inputMode === 'vad') {
      try {
        await this._audioCapture.start();
        this._micAvailable = true;
      } catch {
        this._micAvailable = false;
        this._textMode = true;
      }
    }
  }

  private _stopAudio(): void {
    if (this._audioCapture) {
      this._audioCapture.stop();
      this._audioCapture = null;
    }
    if (this._audioPlayback) {
      this._audioPlayback.destroy();
      this._audioPlayback = null;
    }
  }

  private _onAudioChunk = (data: ArrayBuffer): void => {
    if (!this._sessionId || this._isMuted) return;
    const bytes = new Uint8Array(data);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    wsClient.sendVoiceAudioChunk(this._sessionId, btoa(binary));
  };

  private _onWsMessage = (e: Event): void => {
    const detail = (e as CustomEvent).detail as { type: string; payload: Record<string, unknown> };
    const { type, payload } = detail;

    switch (type) {
      case 'voice:call:started':
        this._sessionId = payload.sessionId as string;
        this._callState = 'active';
        this._startDurationTimer();
        void this._initAudio();
        break;

      case 'voice:call:ended':
        if (payload.sessionId === this._sessionId) {
          this._callState = 'ended';
          this._stopDurationTimer();
          this._stopAudio();
          this._dispatchCallEnded();
        }
        break;

      case 'voice:tts:chunk':
        if (payload.sessionId === this._sessionId && this._audioPlayback) {
          this._audioPlayback.enqueue(payload.audio as string);
        }
        break;

      case 'voice:error':
        if (payload.sessionId === this._sessionId || !this._sessionId) {
          this._callState = 'error';
          this._errorMessage = (payload.error as string) ?? 'Unbekannter Fehler';
          this._stopDurationTimer();
          this._stopAudio();
        }
        break;
    }
  };

  private _dispatchCallEnded(): void {
    this.dispatchEvent(new CustomEvent('voice-call-ended', {
      detail: { conversationId: this.conversationId },
      bubbles: true,
      composed: true,
    }));
  }

  private _onHangUp(): void {
    if (this._sessionId && this._callState === 'active') {
      wsClient.sendVoiceCallEnd(this._sessionId);
      // voice:call:ended event will trigger _dispatchCallEnded
    } else {
      this._stopAudio();
      this._stopDurationTimer();
      this._dispatchCallEnded();
    }
  }

  private _onMuteToggle(): void {
    this._isMuted = !this._isMuted;
    if (this._isMuted) {
      this._audioCapture?.stop();
    } else if (this._inputMode === 'vad') {
      this._audioCapture?.start().catch(() => {});
    }
  }

  private _onModeChange(e: CustomEvent<{ mode: InputMode }>): void {
    const newMode = e.detail.mode;
    this._inputMode = newMode;
    if (newMode === 'vad' && !this._isMuted) {
      this._audioCapture?.start().catch(() => {});
    } else if (newMode === 'ptt') {
      this._audioCapture?.stop();
    }
  }

  private _onPttStart(): void {
    if (!this._isMuted) {
      this._audioCapture?.start().catch(() => {});
    }
  }

  private _onPttEnd(): void {
    this._audioCapture?.stop();
  }

  private _onTextSend(e: CustomEvent<{ text: string }>): void {
    if (this._sessionId && e.detail.text) {
      wsClient.sendVoiceTextMessage(this._sessionId, e.detail.text);
    }
  }

  private _onTextModeToggle(): void {
    this._textMode = !this._textMode;
    if (this._textMode) {
      this._audioCapture?.stop();
    } else if (this._inputMode === 'vad' && !this._isMuted) {
      this._audioCapture?.start().catch(() => {});
    }
  }

  private _startDurationTimer(): void {
    this._callDuration = 0;
    this._durationTimer = setInterval(() => { this._callDuration++; }, 1000);
  }

  private _stopDurationTimer(): void {
    if (this._durationTimer !== null) {
      clearInterval(this._durationTimer);
      this._durationTimer = null;
    }
  }

  private _formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  render() {
    return html`
      <div class="cp-chat-voice-overlay">
        <div class="cp-chat-voice-overlay__panel">

          <!-- Header -->
          <div class="cp-chat-voice-overlay__header">
            <span class="cp-chat-voice-overlay__title">
              ${this._callState === 'connecting' ? 'Verbinde...' : nothing}
              ${this._callState === 'active' ? html`
                Voice-Modus
                <span class="cp-chat-voice-overlay__duration">${this._formatDuration(this._callDuration)}</span>
              ` : nothing}
              ${this._callState === 'ended' ? 'Gespräch beendet' : nothing}
              ${this._callState === 'error' ? 'Verbindungsfehler' : nothing}
            </span>
            <button
              class="cp-chat-voice-overlay__close-btn"
              @click=${() => this._onHangUp()}
              title="Voice-Modus beenden"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="18" height="18">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <!-- Connecting state -->
          ${this._callState === 'connecting' ? html`
            <div class="cp-chat-voice-overlay__connecting">
              <div class="cp-chat-voice-overlay__spinner"></div>
            </div>
          ` : nothing}

          <!-- Error state -->
          ${this._callState === 'error' ? html`
            <div class="cp-chat-voice-overlay__error">
              <p>${this._errorMessage}</p>
              <button class="cp-chat-voice-overlay__retry-btn" @click=${() => this._startCall()}>
                Erneut versuchen
              </button>
            </div>
          ` : nothing}

          <!-- Active call -->
          ${this._callState === 'active' ? html`
            <div class="cp-chat-voice-overlay__visualizers">
              <div class="cp-chat-voice-overlay__viz-row">
                <span class="cp-chat-voice-overlay__viz-label">Du</span>
                <cp-audio-visualizer
                  source="user"
                  ?active=${!this._isMuted && !this._textMode}
                ></cp-audio-visualizer>
              </div>
              <div class="cp-chat-voice-overlay__viz-row">
                <span class="cp-chat-voice-overlay__viz-label">Agent</span>
                <cp-audio-visualizer
                  source="agent"
                  ?active=${this._isAgentSpeaking}
                ></cp-audio-visualizer>
              </div>
            </div>

            <cp-call-controls
              ?muted=${this._isMuted}
              input-mode=${this._inputMode}
              ?text-mode=${this._textMode}
              @mute-toggle=${() => this._onMuteToggle()}
              @hang-up=${() => this._onHangUp()}
              @mode-change=${(e: CustomEvent<{ mode: InputMode }>) => this._onModeChange(e)}
              @ptt-start=${() => this._onPttStart()}
              @ptt-end=${() => this._onPttEnd()}
              @text-send=${(e: CustomEvent<{ text: string }>) => this._onTextSend(e)}
              @text-mode-toggle=${() => this._onTextModeToggle()}
            ></cp-call-controls>
          ` : nothing}

        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-chat-voice-overlay': CpChatVoiceOverlay;
  }
}
